// Invite / token landing (handoff 7.1, AC-B-001/B-002/B-025).
//
// The raw invite token lives ONLY in the URL fragment (#t=...). On mount we read
// location.hash, exchange the token at POST /session (body, never query), store
// the non-sensitive session handle, then history.replaceState strips the fragment
// from the address bar and history. Invalid / expired / revoked -> distinct error
// screens, no app entry. The raw token is never stored client-side or logged.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, isDemoMode } from '../lib/api';
import { getSession, setSession } from '../lib/session';
import { BrandMark } from '../components/BrandMark';
import { LandingSkeleton } from '../components/Skeletons';
import { Warning } from '../lib/icons';
import { motion } from 'motion/react';
import { useScreenEnter } from '../lib/motion';

type Phase = 'loading' | 'error' | 'ready';
type ErrKind = 'invalid' | 'expired' | 'revoked' | 'none';

const ERR_COPY: Record<Exclude<ErrKind, 'none'>, { title: string; body: string }> = {
  invalid: { title: '链接无效或已失效', body: '这个反馈链接不正确。请联系 AstraForge 获取新的反馈链接。' },
  expired: { title: '链接已过期', body: '这条反馈链接已超过有效期。请联系 AstraForge 重新获取。' },
  revoked: { title: '链接已被撤销', body: '这条反馈链接已被撤销，不再可用。请联系 AstraForge 获取新链接。' },
};

function classifyError(e: ApiError): Exclude<ErrKind, 'none'> {
  if (e.status === 410) return e.code === 'revoked_token' ? 'revoked' : 'expired';
  if (e.code === 'revoked_token') return 'revoked';
  if (e.code === 'expired_token') return 'expired';
  return 'invalid';
}

export function Invite() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('loading');
  const [err, setErr] = useState<ErrKind>('none');
  const errRef = useRef<HTMLDivElement>(null);
  // Hooks must run in the same order on every render. Calling this only after
  // loading completes causes React error #310 and a blank portal.
  const screenEnter = useScreenEnter();

  useEffect(() => {
    // Already have a live session -> straight to list.
    if (getSession()) {
      navigate('/list', { replace: true });
      return;
    }
    const hash = window.location.hash;
    const m = hash.match(/[#&]t=([^&]+)/);
    const token = m ? decodeURIComponent(m[1]) : '';

    const run = async () => {
      try {
        // Demo mode with no token: bootstrap a synthetic demo session so the
        // portal is explorable locally (mock-flagged per AC-X-003). Live mode
        // with no token shows a guidance screen (invite-only).
        const effective = token || (isDemoMode() ? 'demo' : '');
        if (!effective) {
          setPhase('ready');
          return;
        }
        const handle = await api.bootstrapSession(effective);
        setSession(handle);
        // React Router owns the history entry. Navigating to a hash-free route
        // strips the raw token without discarding Router's internal state.
        navigate('/list', { replace: true });
      } catch (e) {
        setErr(classifyError(e as ApiError));
        setPhase('error');
        window.history.replaceState(null, '', window.location.pathname);
        // Announce the error and move focus to it.
        setTimeout(() => errRef.current?.focus(), 0);
      }
    };
    run();
  }, [navigate]);

  if (phase === 'loading') {
    return (
      <main className="app-main">
        <LandingSkeleton />
      </main>
    );
  }

  if (phase === 'error' && err !== 'none') {
    const copy = ERR_COPY[err];
    return (
      <main className="app-main">
        <motion.section
          className="section"
          {...screenEnter}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '1rem' }}
        >
          <BrandMark size={32} className="text-accent" />
          <div
            ref={errRef}
            className="callout callout-danger"
            role="alert"
            aria-live="polite"
            tabIndex={-1}
            style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', textAlign: 'left' }}
          >
            <Warning weight="regular" aria-hidden="true" style={{ marginTop: 2 }} />
            <div>
              <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{copy.title}</strong>
              <span style={{ color: 'var(--text-2)' }}>{copy.body}</span>
            </div>
          </div>
          <p className="muted" style={{ fontSize: '0.8125rem' }}>联系邮箱：support@astraforge.online</p>
        </motion.section>
      </main>
    );
  }

  // Live mode, no token, no session: invite-only guidance.
  return (
    <main className="app-main">
      <motion.section
        className="section"
        {...screenEnter}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '1rem' }}
      >
        <BrandMark size={32} className="text-accent" />
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, letterSpacing: '-0.01em' }}>AstraForge 反馈</h1>
        <p className="muted prose-measure">请通过 AstraForge 发送给您的邀请链接进入反馈门户。</p>
        <div className="callout callout-accent prose-measure">
          还没有合作项目？您可以先提交合作需求，我们会通过邮件完成初步评估后联系您。
        </div>
        <button type="button" className="btn btn-primary" onClick={() => navigate('/cooperate')}>
          提交评估
        </button>
      </motion.section>
    </main>
  );
}
